import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Switch } from "@progress/kendo-react-inputs";
import { button, icon, label, notifyIcon, number, quote } from "../../../config/constants";
import { deleteWorkflowForm, editWorkflowForm, generateFormAiDescription, getWorkflowFormById } from "../../../shared/services/workflow.services";
import CustomPrivacyMultiSelect from "../CustomPrivacyMultiSelect";
import { useFormMetadata } from "../contexts/FormMetadataContext";
import { createPrivacyItemRender, getManipulatedPrivacyList, getPrivacyOptionDisplayLabel } from "./formSettings.helper";

const FormPrivacy = ({
  projectId,
  formId,
  currentFormData,
  privacyOptions,
  privacyLevel,
  isNewForm,
  userOpenedCustomModeRef,
  setSelectedTeams,
  setIsUsingCustomPrivacy,
  isUsingCustomPrivacy,
  setPrivacyLevel,
  selectedTeams,
  onPrivacyChange,
  teamData,
  workflowId: workflowIdFromRoute,
  onEditWorkflow,
}) => {
  const [dropdownRenderKey, setDropdownRenderKey] = useState(0);
  const [isCustomPrivacyMode, setIsCustomPrivacyMode] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const { allProjectsList } = useSelector((state) => state.tasks.defaultDetails);
  const dispatch = useDispatch();
  const currentProject = allProjectsList?.find((p) => p.ProjectId == parseInt(projectId));
  const queueName = currentProject?.QueueName;
  const currentProjectName = currentProject?.ProjectName;
  const privacyNames = useMemo(
    () => ({ companyName: user?.companyName, queueName, currentProjectName }),
    [user?.companyName, queueName, currentProjectName]
  );

  const manipulatedPrivacyDropdownData = useMemo(() => {
    const isMyProjectWorkspace = parseInt(String(projectId), 10) === user?.myProjectId;
    const optionsForDropdown = isMyProjectWorkspace ? privacyOptions?.filter((p) => p.value !== 2) : privacyOptions;
    return getManipulatedPrivacyList(optionsForDropdown, privacyNames);
  }, [projectId, user?.myProjectId, privacyOptions, privacyNames]);
  const { formPrivacy, setFormPrivacy, setAllowedProjectIds } = useFormMetadata();
  const currentDropdownValue = useMemo(() => {
    if (privacyLevel?.value === number.FOUR) {
      let count = 0;
      if (isNewForm || isUsingCustomPrivacy) {
        const uniqueTeamIds = [...new Set((selectedTeams || []).map((team) => team.TeamId).filter((id) => id != null))];
        count = uniqueTeamIds.length;
      } else if (currentFormData?.allowedProjectIds && Array.isArray(currentFormData.allowedProjectIds)) {
        count = currentFormData.allowedProjectIds.length;
      }
      return {
        label: `Custom (${count} Selected)`,
        value: 4,
        isCustom: true,
      };
    }

    if (!privacyLevel?.value || !privacyOptions) {
      return privacyLevel || privacyOptions?.[0] || null;
    }

    const foundItem = privacyOptions.find((item) => item.value === privacyLevel.value);
    if (!foundItem) {
      return privacyLevel;
    }
    return {
      ...foundItem,
      label: getPrivacyOptionDisplayLabel(foundItem, privacyNames),
    };
  }, [
    privacyLevel?.value,
    selectedTeams?.length,
    currentFormData?.allowedProjectIds?.length,
    isUsingCustomPrivacy,
    isNewForm,
    privacyOptions,
    privacyNames,
  ]);

  /**
   * Handles the change event of the privacy dropdown.
   * @param {React.ChangeEvent<HTMLSelectElement>} e - The change event.
   * @author Abhishek Dhillon
   */
  const handlePrivacyDropdownChange = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue?.isCustom || selectedValue?.value === 4) {
      userOpenedCustomModeRef.current = true;
      setIsCustomPrivacyMode(true);
      return;
    }
    setIsUsingCustomPrivacy(false);
    setIsCustomPrivacyMode(false);
    setSelectedTeams([]);
    userOpenedCustomModeRef.current = false;
    if (selectedValue) {
      setPrivacyLevel(selectedValue);
      handleFormPrivacyChange(selectedValue.value);
    }
  };

  /**
   * Handles the change event of the privacy dropdown.
   * @param {React.ChangeEvent<HTMLSelectElement>} e - The change event.
   * @author Abhishek Dhillon
   */
  const handleFormPrivacyChange = async (privacyValue) => {
    try {
      const updatePayload = {
        id: formId,
        workflowId: currentFormData?.workflowId,
        privacy: privacyValue,
      };
      if (isNewForm) {
        setFormPrivacy(privacyOptions?.find((p) => p.value === privacyValue));
        setPrivacyLevel(privacyOptions?.find((p) => p.value === privacyValue));
        return;
      }
      const response = await dispatch(editWorkflowForm(updatePayload));
      if (response) {
        setPrivacyLevel(privacyOptions?.find((p) => p.value === privacyValue));
        onPrivacyChange?.(privacyValue);
      }
    } catch (error) {
      setFormPrivacy(null);
      setPrivacyLevel(null);
    }
  };

  /**
   * Handles the back from custom mode.
   * @author Abhishek Dhillon
   */
  const handleBackFromCustomMode = () => {
    setIsCustomPrivacyMode(false);
    if (selectedTeams?.length > 0) {
      setIsUsingCustomPrivacy(true);
      setPrivacyLevel({
        label: `Custom (${selectedTeams.length} Selected)`,
        value: 4,
        isCustom: true,
      });
    }
    setDropdownRenderKey((prev) => prev + 1);
    if (!isNewForm) {
      userOpenedCustomModeRef.current = false;
    }
  };

  /**
   * Handles the custom item click.
   * @param {React.MouseEvent<HTMLDivElement>} e - The click event.
   * @author Abhishek Dhillon
   */
  const handleCustomItemClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    userOpenedCustomModeRef.current = true;
    setIsCustomPrivacyMode(true);
  }, []);

  /**
   * Handles the privacy item render.
   * @author Abhishek Dhillon
   */
  const privacyItemRender = useMemo(() => createPrivacyItemRender(handleCustomItemClick), [handleCustomItemClick]);

  const resolvedWorkflowId = currentFormData?.workflowId ?? workflowIdFromRoute;

  return (
    <div className='form-setting-item'>
      <div className='d-flex justify-content-between align-items-center'>
        <div className='setting-label'>{label.PRIVACY}</div>
        {resolvedWorkflowId && onEditWorkflow ? (
          <div className='link-text cursor-pointer' onClick={onEditWorkflow}>
            {button.EDIT_WORKFLOW}
          </div>
        ) : null}
      </div>
      <div className='setting-control'>
        <div className='d-flex align-items-center w-100'>
          {!isCustomPrivacyMode ? (
            <DropDownList
              data={manipulatedPrivacyDropdownData}
              textField='label'
              dataItemKey='value'
              value={currentDropdownValue}
              onChange={handlePrivacyDropdownChange}
              className='privacy-dropdown w-100'
              itemRender={privacyItemRender}
              key={`privacy-${privacyLevel?.value}-${selectedTeams?.length || 0}-${isUsingCustomPrivacy ? "1" : "0"}-${dropdownRenderKey}`}
            />
          ) : (
            <>
              <button onClick={handleBackFromCustomMode} title='Back' className='cursor-pointer mr-2 border-0 p-1'>
                {icon.ARROW_LEFT}
              </button>
              <CustomPrivacyMultiSelect
                formId={formId}
                isNewForm={isNewForm}
                currentFormData={currentFormData}
                privacyLevel={privacyLevel}
                setPrivacyLevel={setPrivacyLevel}
                onPrivacyChange={onPrivacyChange}
                setAllowedProjectIds={setAllowedProjectIds}
                teamData={teamData}
                selectedTeams={selectedTeams}
                setSelectedTeams={setSelectedTeams}
                setIsUsingCustomPrivacy={setIsUsingCustomPrivacy}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormPrivacy;
